/**
 * AgencyTaskThread — the two-party messaging surface for an agency_task (#379, Coordination
 * Re-Arch Phase 3b-2c). Rendered INSIDE AgencyTaskDetailBody for BOTH parties — the PEOC
 * requester and the tasked target — so a task carries its conversation in the COP, not on the
 * phone. Reuses the shipped MissionThreadPanel (the generic two-party thread): the agency-task
 * viewer role maps to the thread party (requester→'requestor', target→'operator'), and the
 * three agency-task message client fns drive load / send / markRead. No "Call pilot" link
 * (callPhone omitted) — a task's counterparty is an agency, not a published pilot number.
 *
 * It returns the MissionThreadPanel <section> directly (no padding wrapper): unlike
 * DroneMissionThread — a SidePanel sibling of the mission detail — this mounts within the
 * detail body's already-padded `space-y-4` section stack, so its border-t section lines up
 * with the body's other sections.
 */
import { type JSX } from 'react';
import type { AgencyTaskView } from '@tsr/shared-types';
import { fetchAgencyTaskMessages, markAgencyTaskMessageRead, postAgencyTaskMessage } from './api';
import { MissionThreadPanel } from './components/MissionThreadPanel';

export function AgencyTaskThread({
  task,
  viewerRole,
}: {
  task: AgencyTaskView;
  viewerRole: 'requester' | 'target';
}): JSX.Element {
  // The detail body's viewer role maps onto the generic two-party thread party.
  const party = viewerRole === 'requester' ? 'requestor' : 'operator';
  // FIX (#328p2): a task's counterparty is an AGENCY, not a pilot — pass the real agency names
  // so the thread never inherits MessageThread's default "Pilot" label (A.2 party naming).
  const partyLabels = {
    requestor: task.requesting_agency_name ?? 'Requesting agency',
    operator: task.target_agency_name ?? 'Assigned agency',
  } as const;
  return (
    <MissionThreadPanel
      missionId={task.id}
      viewerRole={party}
      load={(signal) => fetchAgencyTaskMessages(task.id, signal)}
      send={(content) => postAgencyTaskMessage(task.id, content)}
      markRead={(msgId) => markAgencyTaskMessageRead(task.id, msgId)}
      partyLabels={partyLabels}
    />
  );
}
